import React, { useEffect, useState } from "react";
import Button from "../../atoms/Button/Button";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RichTextEditor from "../../organisms/RichTextEditor/RichTextEditor";
import Drawer from "../Drawer/Drawer";
import useIsEditMode from "../../../services/hooks/useEditMode";
import { Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export type DisagnosisTextType = "warning" | "advise";

interface DiagnosisTextProps {
  warningText?: string;
  adviseText?: string;
  onTextChange?: (type: DisagnosisTextType, value: string) => void;
}

const DiagnosisText = (props: DiagnosisTextProps) => {
  const { warningText, adviseText, onTextChange } = props;

  const isInEditMode = useIsEditMode();
  const { t } = useTranslation();
  const isTabletView = window.innerWidth < 1440;

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isAdviseOpen, setIsAdviseOpen] = useState(false);
  const [warningTextState, setWarningTextState] = useState(warningText);
  const [adviseTextState, setAdviseTextState] = useState(adviseText);
  const [currentWarningState, setCurrentWarningState] = useState(
    warningText ?? ""
  );
  const [currentAdviseTextState, setCurrentAdviseTextState] = useState(
    adviseText ?? ""
  );

  useEffect(() => {
    if (warningText !== warningTextState || adviseText !== adviseTextState) {
      setIsWarningOpen(false);
      setIsAdviseOpen(false);
    }

    setWarningTextState(warningText);
    setAdviseTextState(adviseText);
  }, [adviseText, adviseTextState, warningText, warningTextState]);

  const handleOpenWarning = () => {
    setIsWarningOpen(true);
  };

  const handleTextSave = (type: DisagnosisTextType, value: string) => {
    if (onTextChange) {
      if (type === "advise" && currentAdviseTextState !== adviseText) {
        onTextChange(type, value);
      }
      if (type === "warning" && currentWarningState !== warningText) {
        onTextChange(type, value);
      }
    }
  };

  return (
    <div className="dui-diagnosis-text">
      {warningText || isInEditMode ? (
        <div>
          <Button
            appearance="error"
            corners="rounded"
            icon={isInEditMode ? <Edit /> : <WarningAmberIcon />}
            className="dui-btn-report-warning"
            onClick={handleOpenWarning}
          >
            {t("careful")}
          </Button>
        </div>
      ) : null}
      {adviseText || isInEditMode ? (
        <div>
          <Button
            corners="rounded"
            className="dui-btn-report-more-info"
            icon={isInEditMode ? <Edit /> : undefined}
            onClick={() => setIsAdviseOpen(true)}
          >
            {t("moreDiagnosisInfo")}
          </Button>
        </div>
      ) : null}

      <Drawer
        isOpen={isWarningOpen}
        onClose={() => setIsWarningOpen(false)}
        widthRefElID={isTabletView ? "dui-main-page" : "dui-doctors-report"}
      >
        <div className="dui-warning-title">{t("careful")}!</div>
        <div className="dui-warning-text">
          <RichTextEditor
            markdown={warningText ?? ""}
            readonly={!isInEditMode}
            onChange={(value) => setCurrentWarningState(value)}
          />
        </div>
        {isInEditMode ? (
          <div className="dui-warning-save-button">
            <Button
              onClick={() => handleTextSave("warning", currentWarningState)}
            >
              {t("save")}
            </Button>
          </div>
        ) : null}
      </Drawer>
      <Drawer
        isOpen={isAdviseOpen}
        onClose={() => setIsAdviseOpen(false)}
        widthRefElID={isTabletView ? "dui-main-page" : "dui-doctors-report"}
      >
        <div className="dui-advise-title">{t("moreDiagnosisInfoTitle")}</div>
        <div className="dui-advise-text">
          <RichTextEditor
            markdown={adviseText ?? ""}
            readonly={!isInEditMode}
            onChange={(value) => setCurrentAdviseTextState(value)}
          />
        </div>
        {isInEditMode ? (
          <div className="dui-warning-save-button">
            <Button
              onClick={() => handleTextSave("advise", currentAdviseTextState)}
            >
              {t("save")}
            </Button>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
};

export default DiagnosisText;
